import { Trans } from '@lingui/macro'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useCallback, useMemo } from 'react'
import { SideBarItem, SideBarListItem } from './ResponsiveSideBar'
import { categorizeSidebarItems } from './SidebarConfig'

const SectionHeader = styled(Typography)(({ theme }) => ({
    color: '#333333',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    padding: '12px 16px 4px 16px',
    opacity: 1
}))

interface OrganizedSideBarProps {
    items: SideBarItem[]
    open: boolean
    onItemClick: (route: string) => void
    activeRoute: string
    sections?: any[]
}

export const OrganizedSideBar: React.FC<OrganizedSideBarProps> = ({ items, open, onItemClick, activeRoute, sections }) => {
    // Memoize categorized items to prevent recalculation on every render
    const categorizedItems = useMemo(() => categorizeSidebarItems(items, sections), [items, sections])
    const homeItems = categorizedItems.home || []
    const applicationItems = categorizedItems.applications || []
    const resourceItems = categorizedItems.resources || []

    // Memoize the click handler to prevent new function creation on every render
    const handleItemClick = useCallback(
        (route: string) => {
            onItemClick(route)
        },
        [onItemClick]
    )

    const renderSection = useCallback(
        (sectionItems: SideBarItem[], sectionTitle?: React.ReactNode, customSx?: any) => (
            <Box
                sx={{
                    marginTop: open ? (sectionTitle ? '16px' : '8px') : '0px',
                    marginBottom: open ? (sectionTitle ? '8px' : '4px') : '0px',
                    ...customSx
                }}
            >
                {sectionTitle && open && <SectionHeader>{sectionTitle}</SectionHeader>}
                {sectionItems.map((item) => (
                    <SideBarListItem
                        key={item.route}
                        route={item.route}
                        name={item.name}
                        icon={item.icon}
                        onClick={() => handleItemClick(item.route)}
                        active={activeRoute.includes(item.route)}
                        open={open}
                        userPermissions={item.userPermissions}
                        permissions={item.permissions}
                    />
                ))}
            </Box>
        ),
        [open, activeRoute, handleItemClick]
    )

    return (
        <Box
            sx={{
                backgroundColor: '#f5f6f6',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                overflowX: open ? 'auto' : 'hidden'
            }}
        >
            <Box sx={{ flexGrow: 1 }}>
                {renderSection(homeItems, undefined, {
                    marginTop: open ? '12px' : '0px',
                    marginBottom: open ? '2px' : '0px'
                })}
                {renderSection(applicationItems, <Trans>Applications</Trans>, {
                    marginTop: open ? '8px' : '0px'
                })}
            </Box>
            {resourceItems.length > 0 && <Box sx={{ marginTop: 'auto' }}>{renderSection(resourceItems, <Trans>Resources</Trans>)}</Box>}
        </Box>
    )
}
